import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { toastApiError } from "../../../../api/apiError"
import {
  usePatchSupplier,
  useSupplierDetail,
} from "../../../../api/suppliersApi"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface AddSupplierEmailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplierId: string
  supplierName: string
  supplierNumber?: string
  onSaved: (email: string) => void
}

// Shown when a supplier has NO email at all in master data — i.e. this is the
// first email ever set for that supplier. Because there's nothing to "add to"
// yet, saving here updates the supplier's master-data record itself (not just
// this draft's recipient list), so every future inquiry/declaration for the
// supplier picks it up too.
export function AddSupplierEmailModal({
  open,
  onOpenChange,
  supplierId,
  supplierName,
  supplierNumber,
  onSaved,
}: AddSupplierEmailModalProps) {
  const { translate } = useTranslation()
  const { mutate: patchSupplier, isPending } = usePatchSupplier()
  // Master data is the source of truth for name/number — the inquiry draft
  // itself doesn't carry a supplier number, so refetch it here for display.
  const { data: supplierDetail } = useSupplierDetail(open ? supplierId : null)
  const displayName = supplierDetail?.name ?? supplierName
  const displayNumber = supplierDetail?.supplierNumber ?? supplierNumber
  const [email, setEmail] = useState("")
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setEmail("")
      setError(null)
    }
  }, [open])

  function handleSave() {
    const v = email.trim()
    if (!EMAIL_REGEX.test(v)) {
      setError(translate("MC_INQ_DRAFT_INVALID_EMAIL"))
      return
    }
    patchSupplier(
      { id: supplierId, changes: { email: v } },
      {
        onSuccess: () => {
          onSaved(v)
          onOpenChange(false)
        },
        onError: (err) => {
          toastApiError(err, translate, "MC_API_ERROR_GENERIC")
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {translate("MC_INQ_DRAFT_ADD_SUPPLIER_EMAIL_TITLE")}
          </DialogTitle>
          <DialogDescription>
            {translate("MC_INQ_DRAFT_ADD_SUPPLIER_EMAIL_DESC")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-[11.5px] font-semibold text-grey5">
              {translate("MC_INQ_DRAFT_ADD_SUPPLIER_EMAIL_SUPPLIER_LABEL")}
            </span>
            <span className="text-[13px] text-white">{displayName}</span>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[11.5px] font-semibold text-grey5">
              {translate("MC_INQ_DRAFT_ADD_SUPPLIER_EMAIL_NUMBER_LABEL")}
            </span>
            <span className="font-mono text-[13px] text-white">
              {displayNumber || "—"}
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="supplier-email-input"
              className="text-[11.5px] font-semibold text-grey5"
            >
              {translate("MC_INQ_DRAFT_ADD_SUPPLIER_EMAIL_LABEL")}
            </label>
            <Input
              id="supplier-email-input"
              autoFocus
              value={email}
              placeholder={translate("MC_INQ_DRAFT_ADD_EMAIL_PLACEHOLDER")}
              aria-invalid={!!error}
              onChange={(e) => {
                setEmail(e.target.value)
                if (error) setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  handleSave()
                }
              }}
            />
            {error && (
              <span role="alert" className="text-[11px] text-red1">
                {error}
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {translate("MC_INQ_DRAFT_BTN_CANCEL")}
          </Button>
          <Button
            className="bg-yellow1 text-black hover:bg-yellow2"
            onClick={handleSave}
            disabled={isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {translate("MC_INQ_DRAFT_ADD_SUPPLIER_EMAIL_SAVE")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
